import { useState } from 'react';
import { useCandidateHistory } from './hooks/useCandidateHistory';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { History } from 'lucide-react';
import { truncateText, isDuplicateCandidate } from '../../lib/utils';

interface GroupedCandidate {
  id: string;
  name: string;
  cv_text: string;
  created_at: string;
  duplicateCount: number;
  lastSubmitted: string;
}

interface CandidateSelectorProps {
  onSelect: (candidate: { name: string; cvText: string }) => void;
}

export function CandidateSelector({ onSelect }: CandidateSelectorProps) {
  const { candidates, isLoading } = useCandidateHistory();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const groupedCandidates = candidates.reduce<GroupedCandidate[]>((acc, current) => {
    const duplicateIndex = acc.findIndex(group => isDuplicateCandidate(current.name, current.cv_text, [group]));
    if (duplicateIndex >= 0) {
      acc[duplicateIndex].duplicateCount += 1;
      acc[duplicateIndex].lastSubmitted = new Date(Math.max(new Date(acc[duplicateIndex].lastSubmitted).getTime(), new Date(current.created_at).getTime())).toISOString();
    } else {
      acc.push({ ...current, duplicateCount: 1, lastSubmitted: current.created_at });
    }
    return acc;
  }, []);

  if (isLoading) {
    return <Button variant="outline" size="sm" disabled>Loading History...</Button>;
  }

  if (!candidates.length) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="justify-between w-[200px] text-muted-foreground hover:text-foreground"
        >
          <History className="mr-2 h-4 w-4 flex-shrink-0 text-primary" />
          Select Previous...
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0">
        <Command>
          <CommandInput 
            placeholder="Search candidate..." 
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>No matching candidate found.</CommandEmpty>
            <CommandGroup heading="Previous Candidates">
              <ScrollArea className="h-[200px]">
                {groupedCandidates
                  .filter(candidate => candidate.name.toLowerCase().includes(searchValue.toLowerCase()))
                  .map((candidate) => (
                    <CommandItem
                      key={candidate.id}
                      value={candidate.name}
                      onSelect={() => {
                        onSelect({ name: candidate.name, cvText: candidate.cv_text });
                        setOpen(false);
                        setSearchValue("");
                      }}
                      className="cursor-pointer flex flex-col items-start px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
                    >
                      <div className="w-full flex justify-between items-center mb-1">
                        <span className="font-medium text-foreground truncate">{candidate.name}</span>
                        {candidate.duplicateCount > 1 && (
                          <Badge variant="secondary" className="ml-2 flex-shrink-0 bg-emerald-100 text-emerald-700 text-xs px-1.5 py-0">
                            {candidate.duplicateCount}x
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {truncateText(candidate.cv_text, 60)}
                      </p>
                    </CommandItem>
                  ))}
              </ScrollArea>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}